/**
 * Sign-in row — shows `Connected as <upn> · Tenant <tid>` when Entra. Hidden
 * for SQL/Windows auth. Click-to-copy on tenant id.
 *
 * Slice 3 → Section 2 ("Sign-in") of the dialog.
 */

import { useState } from 'react';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';

type SignInRowProps = { report: DiagnosticsReport };

export function DiagnosticsSignInRow({ report }: SignInRowProps) {
  const [copied, setCopied] = useState(false);
  const isEntra =
    report.authKind === 'entraDeviceCode' || report.authKind === 'entraInteractiveBrowser';
  if (!isEntra) return null;

  const upn =
    report.identity?.upn || report.identity?.name || 'unknown';
  const tid = report.identity?.tenantId;

  const handleCopy = async () => {
    if (!tid) return;
    const ok = await writeClipboardText(tid);
    if (ok) {
      setCopied(true);
      toast.success({ title: 'Tenant ID copied to clipboard.' });
      window.setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error({ title: 'Failed to copy tenant ID.' });
    }
  };

  return (
    <p className="text-sm" data-testid="diag-sign-in-row">
      <span className="text-muted-foreground">Connected as </span>
      <span className="font-medium">{upn}</span>
      {tid && (
        <>
          <span className="text-muted-foreground"> · Tenant </span>
          <button
            type="button"
            onClick={handleCopy}
            className="font-mono text-xs underline-offset-4 hover:underline"
            title="Click to copy tenant id"
            data-testid="diag-tenant-copy"
          >
            {tid}
          </button>
          {copied && <span className="ml-1 text-xs text-muted-foreground">(copied)</span>}
        </>
      )}
    </p>
  );
}
